<script>

import Apps from '../../../core/Apps';
import DatePicker from '../global/form/DatePicker.vue';
import {http} from '../../http';
import isEmpty from 'lodash/isEmpty';
import SurveyRequestAnswers from "../apps/survey/SurveyRequestAnswers";
import printJournalsMixin from "@/system/helpers/journalPrintToPdf";
import JournalTemplateSelect from 'Components/global/JournalTemplateSelect.vue';
import replaceContentTags from '@/system/helpers/replaceContentTags.js';
import TreatmentSelector from 'Components/apps/treatments/TreatmentSelector.vue';
import SurveyRequestDeleteModal from '@/system/Models/SurveyRequest/SurveyRequestDeleteModal.vue'
import Switchery from 'Components/global/form/Switchery.vue';
import { trans } from '@/system/utils/translations';
import { createJournalMentionConfig } from '@/ckeditor/plugins/mentionActions/helpers';
import NavigationAlert from 'Components/global/NavigationAlert.vue';
import { mapStores } from 'pinia';
import { useJournalsStore } from 'Components/journals/store/journals';

export default {

    mixins: [printJournalsMixin],
    data() {
        return {
            // This is needed to allow reactivity in the component, even though it's the same reference
            journal: this.initialJournal,
            loading: false,
            editedJournal: {},
            editing: false,
            richTextEditorId: 'journal-content-editor',
            action: null,
            reason: null,
            uploadCount: 0,
        }
    },

    props: {
        clientOrGroup: {
            required: true,
            type: Object,
        },
        initialJournal: {
            required: true,
        },
        bookings: {
            required: false,
            type: Array,
        },
        openJournalEditForm: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    mounted() {
        if (this.isTreatmentsActive && !this.isGroupJournal) {
            this.$store.dispatch('clientTreatments/loadTreatments', this.clientOrGroup.id)
        }

        this.$store.dispatch('journals/loadTemplates');

        this.$event_bus.$on('journalFavorited', (journal_id, marked_as_favorite) => {
            this.journal.favorite = this.journal.id === journal_id && marked_as_favorite;
        });

        this.$event_bus.$on('reasonSubmittedJournals', ({reason, itemId}) => {
            if (this.journal.id === itemId) {
                this.reason = reason

                switch (this.action) {
                    case 'delete':
                        this.deleteJournalEntry()
                        break;

                    case 'lock':
                    case 'unlock':
                        this.lockOrUnlockJournalEntry()
                        break;
                }
            }
        })

        this.$event_bus.$on('closeReasonModal', () => {
            this.closeReasonModal()
        })

        this.$event_bus.$on('openJournalEntry', (journalId) => {
            this.handleJournalOpen(journalId);
        });

        this.$emit('journalMounted');

        if (this.openJournalEditForm) {
            this.handleJournalOpen(this.journal.id);
        }
    },

    computed: {
        ...mapStores(useJournalsStore),

        can_edit_journals() {
            return this.$can('edit-journals');
        },

        canEditSurveys() {
            return this.$can('edit-surveys');
        },

        isDirty() {
            return this.editedJournal.content !== this.journal.content || this.editedJournal.title !== this.journal.title || this.editedJournal.date !== this.journal.date;
        },

        hasUnsavedChanges() {
            return this.editing && this.isDirty;
        },

        fixed_journal_content() {
            // replace all single spaces with an actual space, so it can break to new lines
            let reg = new RegExp('\\w(&nbsp;)\\w', 'gi');
            html = this.journal.content.replace(reg, (match, group) => match.replace(group, ' '));

            let el = $('<div></div>').html(this.sanitizeHtml(html));
            el.find('table:not(.table)').removeAttr('align').addClass('table').wrap('<div class="table-responsive"></div>');
            el.append($('<div class="clear-both"></div>'));

            return el.html();
        },

        isGroupJournal() {
            return this.journal.client_group_id > 0;
        },

        isAspitUser() {
            return ['physica', 'psykbase'].includes(this.$store.state.brand);
        },

        journal_lock_tooltip() {
            return this.journal.locked ? trans('client.journal_unlock_entry') : trans('client.journal_lock_entry')
        },

        isTreatmentsActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.Treatments);
        },

        treatments() {
            return this.$store.getters['clientTreatments/active'];
        },

        isBookingDeleted() {
            return this.journal.is_booking_deleted && !this.journal.is_survey;
        },

        user() {
            return this.$store.state.auth.user;
        },

        editorTagsConfig() {
            return {
                types: this.$store.getters['journals/journalTags']
            };
        },

        journalTemplates() {
            return this.$store.getters['journals/templates'].slice(0).sort((a, b) => {
                if (a.order > b.order) {
                    return 1;
                }

                if (a.order < b.order) {
                    return -1;
                }

                if (a.order == b.order) {
                    return a.title.localeCompare(b.title, locale || 'en');
                }
            });
        },

        contentJournalEditorConfig() {
            const config = {
                toolbar: [
                    'undo', 'redo',
                    '|',
                    'bold', 'italic', 'underline', 'strikethrough',
                    '|',
                    'numberedList', 'bulletedList',
                    '|',
                    'alignment',
                    '|',
                    'heading',
                    '|',
                    'link',
                    '|',
                    'insertTable',
                    'imageUpload',
                    '|',
                    'tags'
                ],

                tagsConfig: this.editorTagsConfig,
            };

            if (this.isNewJournalExperienceEnabled) {
                config.toolbar.push('templates');

                config.templatesConfig = {
                    types: this.journalTemplates,
                    tagReplacements: this.$store.getters['journals/getTagReplacementsForClientOrGroup'](this.clientOrGroup, this.isGroupJournal),
                };

                config.mention = createJournalMentionConfig({
                    tags: this.$store.getters['journals/journalTags'],
                    templates: this.journalTemplates,
                });
            }

            return config;
        },

        isNewJournalExperienceEnabled() {
            return this.$store.state.featureFlags.new_journal_experience;
        },

        lockedBySystem() {
            return this.journal.locked === true && this.journal.locked_by_user_id === 0;
        },
    },

    methods: {
        trans,
        isEmpty,

        getDateFormatted(date) {
            return moment(date, "DD-MM-YYYY").locale(moment_locale).format('LL');
        },

        is_usermanagement_enabled() {
            return this.$store.getters['apps/isAppEnabled'](Apps.UserManagement)
                || this.$store.getters['apps/isAppEnabled'](Apps.Tenants);
        },

        selectJournalTemplate(template) {
            if (!template) return;

            const tagReplacements = this.$store.getters['journals/getTagReplacementsForClientOrGroup'](this.clientOrGroup, this.isGroupJournal);
            this.editedJournal.content += replaceContentTags(template.content, tagReplacements);
        },

        getJournalDateFormatted(date) {
            if (!date) {
                return false;
            }

            return trans('client.journal_created_at') + ' ' + moment(date, "YYYY-MM-DD HH:mm:ss").locale(moment_locale).format('LLL');
        },

        markAsFavorite(journal_id, mark_as_favorite) {
            this.loading = true;

            http.post(`/app/journal/${journal_id}/settings`, { favorite: mark_as_favorite })
                .then(({ data }) => {
                    this.loading = false;
                    toastr.success(data.success);

                    this.$event_bus.$emit('journalFavorited', journal_id, mark_as_favorite);
                })
                .catch(({ error, errorBag }) => {
                    this.loading = false;
                    this.$setErrorBag(errorBag);
                });
        },

        lockOrUnlockJournalEntry() {
            const lock = this.action === 'lock' ? 1 : 0

            http.post(`/app/journal/${this.journal.id}/settings`, { lock: lock, reason: this.reason })
                .then(({ data }) => {
                    toastr.success(data.success);

                    if (lock) {
                        this.$event_bus.$emit('journalLocked', this.journal.id, data.journal.locked_at);
                    } else {
                        this.$event_bus.$emit('journalUnlocked', this.journal.id);
                    }

                    this.journal.locked_at = lock ? data.journal.locked_at : null;
                    this.journal.locked = lock;

                    swal.close();
                })
                .catch(({ error, errorBag }) => {
                    swal.close();
                });
        },

        closeReasonModal() {
            this.action = null
            this.reason = null
        },

        getPrintJournalTitle() {
            let page_title = this.clientOrGroup.name;

            if (!this.isGroupJournal && this.clientOrGroup.address) {
                page_title = page_title + ', ' + this.clientOrGroup.address + " " + this.clientOrGroup.zip + " " + this.clientOrGroup.city;
            }

            if (!this.isGroupJournal && this.clientOrGroup.cpr) {
                page_title = page_title + ', ' + trans('clients.social_security') + ': ' + this.clientOrGroup.cpr;
            }

            return page_title;
        },

        getLockedAtDateFormatted(journal) {
            let locked_at_text = trans('client.journal_locked_date');

            if (journal.locked_at) {
                locked_at_text +=  ': ' + moment(journal.locked_at, "YYYY-MM-DD HH:mm:ss").locale(moment_locale).format('LLL');
            }

            if(journal.locked_by_user && this.is_usermanagement_enabled){
                locked_at_text += ' - ' + journal.locked_by_user.realname;
            }

            return locked_at_text;
        },

        printJournalEntry(orientation = 'portrait') {
            this.printIndividualJournal(
                this.journal.id,
                this.journal.is_survey ? 'survey' : 'journal',
                orientation
            );
        },

        deleteJournalEntryPrompt() {
            swal({
                title: trans('settings.delete_entry_title'),
                text: trans('settings.delete_entry'),
                showCancelButton: true,
                cancelButtonText: trans('global.cancel'),
                showLoaderOnConfirm: true,
                closeOnConfirm: false,
            }, () => {
                this.deleteJournalEntry();
            });
        },

        deleteJournalEntry() {
            http.post(`/app/journal/${this.journal.id}/destroy`, {reason: this.reason})
                .then(({ data }) => {
                    toastr.success(data.success);

                    this.$event_bus.$emit('journalDeleted', this.journal.id);

                    swal.close();
                })
                .catch(({ error, errorBag }) => {
                    swal.close();
                });
        },

        editJournal() {
            this.editedJournal = {...this.journal};
            this.editing = true;
            this.$event_bus.$emit('resetJournalTemplateSelection');
        },

        cancelEdit() {
            if (this.isDirty) {
                swal({
                        title: trans('client.unsaved_journal_title'),
                        text: trans('client.unsaved_journal_text'),
                        showCancelButton: true,
                        icon: 'warning',
                        html: true,
                        cancelButtonText: trans('global.close'),
                        confirmButtonText: trans('global.save'),
                    }, (confirmed) => {
                        if (confirmed) {
                            this.saveEdit(this.journal.is_draft);
                        } else {
                            this.editing = false;
                        }
                    });

                if ($('.sweet-alert').css('display') == 'block') {
                    $('.sweet-overlay').click(function(e) {
                        $('.sweet-alert').hide();
                        $('.sweet-overlay').hide();
                        $('body').removeClass('stop-scrolling');
                    });
                }
            } else {
                this.editing = false;
            }
        },

        async saveEdit(saveAsDraft = false) {
            const url = `/app/journal/${this.editedJournal.id}`;
            // Save the edit but do not close the editor
            this.loading = true;

            await http.post(url, { ...this.editedJournal, is_draft: saveAsDraft })
                .then(({ data }) => {
                    this.loading = false;
                    this.$event_bus.$emit('journalUpdated', data.journal);

                    this.errors.clear();
                    toastr.success(data.success);
                    this.journal = data.journal;

                    this.$event_bus.$emit('refreshTaskList');
                })
                .catch(({ error, errorBag }) => {
                    this.loading = false;
                    this.$setErrorBag(errorBag);
                });
        },

        async saveEditAndClose() {
            if (this.isNewJournalExperienceEnabled && typeof window.Intercom === 'function') {
                window.Intercom('startSurvey', this.journalsStore.intercomJournalSurveyId);
            }

            await this.saveEdit();
            this.editing = false;
        },

        keepJournalDraft() {
            http.post(`/app/journal/${this.journal.id}`, {...this.journal, is_draft: false})
                .then(({ data }) => {
                    this.journal.is_draft = data.journal.is_draft;

                    toastr.success(data.success);

                    this.$event_bus.$emit('journalUpdated', this.journal);
                    this.$event_bus.$emit('journalDraftKept');
                })
                .catch(({ error, errorBag }) => {});
        },

        minimizeJournalEntry(toggle) {
            this.journal.minified = toggle;
            this.$event_bus.$emit('journalMinimizedToggled', this.journal.id, toggle);

            http.post(`/app/journal/${this.journal.id}/settings`, { minified: toggle })
                .then(() => {})
                .catch(() => {});
        },

        openReasonModal(action) {
            this.action = action

            if (action === 'unlock' && this.lockedBySystem) {
                toastr.error(trans('client.journal_system_locked_cannot_unlock'));
                return;
            }

            this.$event_bus.$emit('openReasonModal', {action: this.action, window: 'Journals', itemId: this.journal.id})
        },

        onUpload(uploading) {
            if (uploading) {
                this.uploadCount += 1;
            } else {
                this.uploadCount -= 1;
            }

            this.loading = this.uploadCount > 0;
        },

        handleJournalOpen(journalId) {
            if (journalId) {
                if (this.initialJournal.id === journalId && !this.journal.is_draft) {
                    this.toggleJournalState();
                }
                else if (this.editing) {
                    this.editing = false;
                }
            }
        },

        toggleJournalState() {
            if (!this.journal.locked) {
                this.editJournal();
            } else if (this.journal.minified) {
                this.minimizeJournalEntry(0);
            }
        },
    },

    components: {
        Switchery,
        TreatmentSelector,
        JournalTemplateSelect,
        DatePicker,
        SurveyRequestAnswers,
        SurveyRequestDeleteModal,
        NavigationAlert
    },

    watch: {
        initialJournal: {
            handler(newJournal) {
                this.journal = newJournal;
            },
        },
    },
}

</script>

<template id="client-journal-entry">
    <div class="journal-item card-box bg-box"
        :class="{ 'favorite-journal-entry bordered' : journal.favorite, 'journal-item-draft' : journal.is_draft}"
    >
        <div class="journal-item-information">
            <div class="row">
                <div :class="!editing ? 'col-xs-6' : ''">
                    <ul class="pull-left nav nav-pills" v-if="can_edit_journals && !journal.is_draft && !journal.is_survey && !editing">
                        <li v-if="journal.minified">
                            <a href="" v-on:click.prevent="minimizeJournalEntry(0)" class="toogle-journal-item">
                                <i  class="zmdi zmdi-plus-circle m-l-5 m-r-5"></i>
                            </a>
                        </li>

                        <li v-if="!journal.minified" >
                            <a href="" v-on:click.prevent="minimizeJournalEntry(1)" class="toogle-journal-item">
                                <i  class="zmdi zmdi-minus-circle m-l-5 m-r-5"></i>
                            </a>
                        </li>
                    </ul>

                    <div>
                        <div v-if="editing" class="m-l-10 m-r-10">
                            <div class="row m-b-15">
                                <div class="col-xs-12" :class="{ 'col-sm-8': isNewJournalExperienceEnabled, 'col-sm-5': !isNewJournalExperienceEnabled }">
                                    <div class="form-group m-b-0">
                                        <label  class="control-label">{{ trans('client.journal_title') }}</label>
                                        <input name="title" type="text" class="form-control" v-model="editedJournal.title">
                                    </div>
                                </div>

                                <div class="col-xs-12" :class="{ 'col-sm-4': isNewJournalExperienceEnabled, 'col-sm-2': !isNewJournalExperienceEnabled }">
                                    <div class="form-group m-b-0" v-bind:class="{ 'has-error' : errors.has('date') }">
                                        <label  class="control-label">{{ trans('client.journal_date_label') }}</label>
                                        <date-picker name="date" v-model="editedJournal.date"></date-picker>
                                        <span v-show="errors.has('date')" class="help-block m-l-5" style="display:inline;">{{ errors.first('date') }}</span>
                                    </div>
                                </div>

                                <journal-template-select
                                    v-if="!isNewJournalExperienceEnabled"
                                    class="self-center col-xs-12 col-sm-5"
                                    @templateSelected="selectJournalTemplate"
                                />
                            </div>

                            <div
                                v-if="editedJournal.related_user_id === user.id"
                                class="row"
                            >
                                <div class="col-xs-12">
                                    <switchery v-model="editedJournal.is_private">
                                        <div class="private-journal-switch-content">
                                        <span style="font-size: 14px;">
                                            {{ trans('apps.journals_private') }}
                                        </span>

                                            <i
                                                class="zmdi zmdi-help ml-5 text-lg"
                                                aria-describedby="privateSwitchTooltip"
                                            />

                                            <nord-tooltip id="privateSwitchTooltip">
                                                {{ trans('apps.journals_private_tooltip') }}
                                            </nord-tooltip>
                                        </div>
                                    </switchery>
                                </div>
                            </div>

                            <div class="row">
                                <div
                                    v-if="isTreatmentsActive && treatments.length > 0 && !isGroupJournal"
                                    class="col-xs-12 col-sm-5"
                                >
                                    <treatment-selector
                                        v-model="editedJournal.treatment_id"
                                        :treatments="treatments"
                                        :show-appointments="false">
                                    </treatment-selector>
                                </div>

                                <div
                                    v-if="bookings && bookings.length > 0"
                                    class="col-xs-12 col-sm-5"
                                >
                                    <div class="form-group">
                                        <label for="booking_id">{{ trans('apps.journals_related_appointment') }}</label>
                                        <select
                                            id="booking_id"
                                            v-model="editedJournal.booking_id"
                                            name="booking_id"
                                            class="form-control"
                                        >
                                            <option value="">
                                                {{ trans('apps.journals_no_related_appointment') }}
                                            </option>
                                            <option
                                                v-for="booking in bookings"
                                                :key="booking.id"
                                                :value="booking.id"
                                                :selected="editedJournal.booking_id === booking.id"
                                            >
                                                {{ $filters.humanize_datetime(booking.start) }} {{ booking.product ? `- ${booking.product}` : '' }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="row" v-if="!isEmpty(editedJournal)">
                                <div class="col-xs-12">
                                    <div class="form-group" :class="{'has-error': errors.has('content')}">

                                        <label class="m-r-5 control-label">{{ trans('follow_up.content') }}</label>
                                        <rich-text-editor
                                            :oembedSupport="true"
                                            :id="richTextEditorId"
                                            name="content"
                                            v-model="editedJournal.content"
                                            :config="contentJournalEditorConfig"
                                            uploadResourceType="journal"
                                            :uploadResourceId="editedJournal.id"
                                            @uploading="onUpload"
                                        ></rich-text-editor>
                                        <span class="help-block" v-if="errors.has('content')">{{ errors.first('content') }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="flex justify-between items-center flex-wrap gap-5">
                                <nord-button
                                    type="submit"
                                    @click.prevent="cancelEdit()"
                                    :disabled="loading"
                                >
                                    <nord-icon slot="start" name="interface-close-small" />
                                    {{ trans('global.cancel') }}
                                </nord-button>

                                <div class="flex gap-5">
                                    <nord-button @click.prevent="saveEdit(journal.is_draft)" :disabled="loading">
                                        {{ journal.is_draft ? trans('global.save_draft') : trans('global.save') }}
                                    </nord-button>

                                    <nord-button
                                        variant="primary"
                                        type="submit"
                                        @click.prevent="saveEditAndClose()"
                                        :disabled="loading"
                                    >
                                        <nord-icon slot="start" name="file-patient-records" />
                                        {{ trans('global.save_and_close') }}
                                    </nord-button>
                                </div>
                            </div>
                        </div>

                        <div v-else>
                            <h5 class="m-l-5 float-left">
                                <template v-if="journal.booking_id">
                                    <template v-if="isBookingDeleted">
                                        <span class="mr-2">
                                            {{ journal.title }} ({{ trans('user.booking_deleted') }})
                                        </span>
                                    </template>
                                    <template v-else>
                                        <a
                                            href=""
                                            class="mr-2"
                                            @click.prevent="$event_bus.$emit('showClientAppointmentOverview', journal.booking_id)"
                                        >
                                            {{ journal.title }}
                                        </a>
                                    </template>
                                </template>
                                <template v-else>
                                    <span v-if="journal.title" class="mr-2">{{ journal.title }}</span>
                                </template>
                                <span v-if="journal.is_draft">{{ trans('apps.journals_draft') }} - </span>{{ getDateFormatted(journal.date) }}

                                <template v-if="!journal.is_draft">
                                    <span v-if="journal.locked"></span>

                                    <span v-if="journal.related_user && is_usermanagement_enabled">
                                        <i class="fa fa-user m-l-10"
                                            v-tooltip="trans('client.journal_created_by', {'user': journal.related_user.realname})">
                                        </i>
                                    </span>

                                    <span v-if="!journal.is_survey && journal.created_at">
                                        <i class="zmdi zmdi-time m-l-10" v-tooltip="getJournalDateFormatted(journal.created_at)"></i>
                                    </span>

                                    <span v-if="journal.is_private">
                                        <i class="zmdi zmdi-eye-off m-l-10" v-tooltip="trans('apps.journals_private')"></i>
                                    </span>

                                    <i class="fa fa-star m-l-10" v-if="journal.favorite"></i>
                                </template>
                                <template v-if="journal.treatment && $can('see', 'treatment', journal.treatment)">
                                    <i class="fa fa-notes-medical ml-4"
                                       v-tooltip="`${journal.treatment.start_date} - ${journal.treatment.treatment_type}`">
                                    </i>
                                </template>
                            </h5>
                        </div>
                    </div>
                </div>

                <div class="col-xs-6" v-if="!editing">
                    <ul class="pull-right journal-item-actions nav nav-pills flex items-center flex-wrap" v-if="!journal.is_draft && !journal.is_survey">
                        <li>
                            <nord-badge v-if="journal.booking_id && !isBookingDeleted">
                                {{ trans('global.appointment') }}
                            </nord-badge>
                            <nord-badge v-else>
                                {{ trans('apps.general_note') }}
                            </nord-badge>
                        </li>
                        <li v-show="!isBookingDeleted && !journal.locked && can_edit_journals">
                            <a href="" @click.prevent="editJournal" v-tooltip="trans('global.edit')">
                                <i class="zmdi zmdi-edit"></i>
                            </a>
                        </li>

                        <li>
                            <a href=""
                               @click.prevent="$event_bus.$emit('viewJournalUpdates', { clientOrGroup, forGroup: isGroupJournal, journal})"
                               v-tooltip="trans('client.journal_updates_tooltip')"
                            >
                                <i class="zmdi zmdi-time-restore"></i>
                            </a>
                        </li>

                        <li v-if="can_edit_journals && !isGroupJournal">
                            <a href="" v-show="journal.favorite"
                               @click.prevent="markAsFavorite(journal.id, 0)"
                               v-tooltip="trans('client.mark_unfavorite')"
                            >
                                <i class="fa fa-star-half-empty"></i>
                            </a>
                            <a href=""
                               v-show="!journal.favorite"
                               @click.prevent="markAsFavorite(journal.id, 1)"
                               v-tooltip="trans('client.mark_favorite')"
                            >
                                <i class="fa fa-star"></i>
                            </a>
                        </li>

                        <li v-show="can_edit_journals">
                            <template v-if="journal.locked && isAspitUser">
                                <a href="" @click.prevent="openReasonModal('unlock')"
                                    v-tooltip="journal_lock_tooltip"
                                    :data-original-title="journal_lock_tooltip"
                                >
                                    <i class="fa fa-lock-open"></i>
                                </a>
                            </template>
                            <template v-else-if="!journal.locked">
                                <a href="" @click.prevent="openReasonModal('lock')"
                                    v-tooltip="journal_lock_tooltip"
                                    :data-original-title="journal_lock_tooltip"
                                >
                                    <i class="fa fa-lock"></i>
                                </a>
                            </template>
                        </li>

                        <li class="dropdown">
                            <a
                                href="#"
                                aria-haspopup="true"
                                aria-expanded="false"
                                data-toggle="dropdown"
                                class="dropdown-toggle"
                            >
                                <span v-tooltip="trans('client.journal_print_entry')" class="py-3">
                                    <i class="zmdi zmdi-print"></i>
                                </span>
                            </a>
                            <ul class="dropdown-menu dropdown-menu-right">
                                <li><a href="#" @click.prevent="printJournalEntry('portrait')"><span class="text-base">{{ trans('client.portrait') }}</span></a></li>
                                <li><a href="#" @click.prevent="printJournalEntry('landscape')"><span class="text-base">{{ trans('client.landscape') }}</span></a></li>
                            </ul>
                        </li>

                        <li v-if="!journal.locked && can_edit_journals">
                            <a href="" @click.prevent="openReasonModal('delete')" v-tooltip="trans('global.delete')">
                                <i class="fa fa-trash"></i>
                            </a>
                        </li>
                    </ul>

                    <ul class="flex items-center pull-right journal-item-actions nav nav-pills" v-if="journal.is_survey">
                        <li>
                            <nord-badge>
                                {{ trans('surveys.survey_response') }}
                            </nord-badge>
                        </li>
                        <li>
                            <a href=""
                               @click.prevent="printJournalEntry"
                               v-tooltip="trans('client.journal_print_entry')"
                               :data-original-title="trans('client.journal_print_entry')"
                            >
                                <i class="zmdi zmdi-print"></i>
                            </a>
                        </li>

                        <li v-if="canEditSurveys">
                            <survey-request-delete-modal :survey-request="journal"/>
                        </li>
                    </ul>
                </div>

                <div class="col-xs-12" v-if="journal.is_draft && !editing" style="margin-top: -7px;">
                    <div class="p-5">
                        <p>
                            {{ trans('apps.journals_draft_info') }}
                        </p>

                        <div v-if="can_edit_journals">
                            <nord-stack direction="horizontal">
                                <nord-button size="s" @click.prevent="editJournal">
                                    <nord-icon slot="start" name="interface-edit"></nord-icon>
                                    <span class="hidden-xs">{{ trans('apps.journals_draft_edit') }}</span>
                                </nord-button>

                                <nord-button size="s" @click.prevent="keepJournalDraft">
                                    <nord-icon slot="start" name="interface-checked"></nord-icon>
                                    <span class="hidden-xs">{{ trans('apps.journals_draft_keep') }}</span>
                                </nord-button>

                                <nord-button size="s" variant="danger" @click.prevent="deleteJournalEntryPrompt">
                                    <nord-icon slot="start" name="interface-delete"></nord-icon>
                                    <span class="hidden-xs">{{ trans('apps.journals_draft_delete') }}</span>
                                </nord-button>
                            </nord-stack>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="journal-item-content"
            :class="{ 'journal-content-hidden' : journal.minified && can_edit_journals, 'is_draft' : journal.is_draft }"
            :id="'journal-content-' + journal.id"
            v-sanitize-html="fixed_journal_content"
            v-if="!editing && (!journal.is_survey || !journal.survey.is_showed_in_journals)"
        ></div>

        <div class="journal-item-content journal-content-survey"
            :class="{ 'journal-content-hidden' : journal.minified && can_edit_journals }"
            :id="'journal-content-survey-' + journal.id"
            v-else-if="!editing"
        >
            <h4>{{ journal.survey.name }}</h4>

            <div
                v-if="journal.survey.description"
                class="text-muted mb-5"
            >
                {{ journal.survey.description }}
            </div>

            <survey-request-answers :initialSurveyRequest="journal"></survey-request-answers>
        </div>
    </div>

    <navigation-alert
        :show="hasUnsavedChanges"
        :enabled="isNewJournalExperienceEnabled"
    />
</template>
